import { useCallback, useEffect, useMemo, useState } from 'react';
import TLSEncryptionText from 'payments-checkout-lib/components/TLSEncryptionText';
import ContactInfo from '../components/FormFields/ContactInfo';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H4 } from 'foundations-components/transitional/components/H4';
import ShippingInfo from '../components/FormFields/ShippingInfo';
import { addHsTrackingCode, trackFormViewAnalyticEvent } from '../utils/analytics';
import { LAYOUTS, getIframeLayout } from '../utils/queryParams';
import { addGoogleAnalyticsTrackingCode } from '../utils/googleAnalytics';
import { addRecaptchaScript, getIsRecaptchaEnabled } from 'payments-checkout-lib/utils/googleRecaptcha';
import { PaymentElementSection } from '../components/PaymentInfo/PaymentElementSection';
import PaymentAmount from '../components/PaymentInfo/PaymentAmount';
import partition from 'hs-lodash/partition';
import { useSelector } from 'react-redux';
import ConfirmPaymentButton from '../components/ConfirmPaymentButton';
import BillingInfo from '../components/FormFields/BillingInfo';
import FormSubmitErrorMsgs from '../components/FormSubmitErrorMsgs';
import MaybeExpiredFutureLineItemsAlert from '../components/FutureCharges/MaybeExpiredFutureLineItemsAlert';
import Total from '../components/Total/Total';
import { INTERACTION_SECTIONS, INTERACTION_TARGETS } from 'payments-checkout-lib/constants/PageInteractionTracking';
import { useOptionalStates } from '../hooks/OrderSummary/useOptionalStates';
import { useTransactionLimits } from '../hooks/useTransactionLimits';
import { getBuyerOverridesIsLoading } from '../state/BuyerOverrides/selectors';
import { isAtCheckoutLineItem } from '../types/utils/typeguards';
import { getIsAnyRecurring } from '../utils/recurring';
import { getIsZeroCheckoutValid, getCanSubmitPayment } from '../utils/validations';
import { getWalletType } from '../utils/wallet';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import usePageInteractionTracking from '../hooks/usePageInteractionTracking';
import { useSubmitPayment } from '../hooks/submitPayment';
import { useValidationErrors } from '../hooks/useValidationErrors';
import { getShouldCalculateTaxOnAddrFieldChange } from '../hooks/useCalculateTax';
import useCalculateTax from '../hooks/useCalculateTax';
import TermsOfServiceInputs from '../components/MerchantTermsAndConditions/TermsOfServiceInputs';
import TermsOfServiceMessages from '../components/MerchantTermsAndConditions/TermsOfServiceMessages';
import { getCalculateTaxLoading } from '../state/calculateTaxSlice';
import { useTermsOfServiceDisplay } from '../hooks/useTermsOfServiceDisplay';
import { trackCheckoutSessionLoaded } from '../utils/tracking';
import useHandleBnplRedirect from '../hooks/useHandleBnplRedirect';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PaymentContainer = ({
  checkoutSession
}) => {
  var _checkoutSession$chec;
  const submitObject = useSubmitPayment();
  useHandleBnplRedirect();
  const {
    formState,
    dispatch
  } = usePaymentFormContext();
  const stripePaymentMethod = formState.stripePaymentMethod;
  const [showRequiredFieldsAlert, setShowRequiredFieldsAlert] = useState(true);
  const [billedAtCheckoutLineItems] = partition(checkoutSession.lineItems, isAtCheckoutLineItem);
  const {
    selectedItems: selectedOptionalLineItems
  } = useOptionalStates(billedAtCheckoutLineItems);
  const {
    trackFirstFormFieldInteraction
  } = usePageInteractionTracking();
  const {
    isValid,
    loading: validationErrorsLoading
  } = useValidationErrors();
  const {
    limits
  } = useTransactionLimits();
  const minimumAmountForPaymentMethod = limits && limits.minimumAmountForPaymentMethod;
  const maximumAmountForPaymentMethod = limits && limits.maximumAmountForPaymentMethod;
  const isZeroCheckout = getIsZeroCheckoutValid(checkoutSession.subtotalsPublicResponse.paymentAmount, checkoutSession.lineItems);
  const loadingBuyerOverrides = useSelector(getBuyerOverridesIsLoading);
  const isCalculateTaxLoading = useSelector(getCalculateTaxLoading);
  const policySettings = checkoutSession.policySettingsData;
  const {
    shouldShowTermsOfServiceAcknowledgementCheckbox,
    shouldShowClickToCancelCheckbox,
    shouldDisplayPMOFCheckbox,
    shouldDisplayPMOFMessage,
    shouldShowTermsOfServiceInputs,
    shouldShowTermsOfServiceMessages,
    shouldShowFutureChargesMessage
  } = useTermsOfServiceDisplay();
  const submitEnabled = useMemo(() => {
    if (!loadingBuyerOverrides && !isCalculateTaxLoading && isValid !== undefined && !validationErrorsLoading) {
      return getCanSubmitPayment(formState, checkoutSession, isValid, shouldShowTermsOfServiceAcknowledgementCheckbox, shouldDisplayPMOFCheckbox, shouldShowClickToCancelCheckbox, stripePaymentMethod);
    }
    return false;
  }, [checkoutSession, formState, isValid, loadingBuyerOverrides, shouldShowTermsOfServiceAcknowledgementCheckbox, shouldDisplayPMOFCheckbox, stripePaymentMethod, validationErrorsLoading, isCalculateTaxLoading, shouldShowClickToCancelCheckbox]);
  const walletType = getWalletType(stripePaymentMethod);
  const collectShippingAddress = checkoutSession.shippableCountries && !!checkoutSession.shippableCountries.length;
  const collectFullBillingAddress = checkoutSession.collectFullBillingAddress;
  const requireBillingAddressForACH = checkoutSession.requireBillingAddressForACH;
  const iFrameLayout = getIframeLayout();
  const isATPortal = checkoutSession.isATPortal;
  const checkoutIntegrations = checkoutSession.checkoutIntegrations;
  const googleAnalyticsData = checkoutIntegrations && checkoutIntegrations.googleAnalyticsIntegration;
  const isRecaptchaEnabled = getIsRecaptchaEnabled({
    liveMode: checkoutSession.liveMode,
    recaptchaIntegration: (_checkoutSession$chec = checkoutSession.checkoutIntegrations) === null || _checkoutSession$chec === void 0 ? void 0 : _checkoutSession$chec.recaptchaIntegration
  });
  const isPartialPaymentsEnabled = checkoutSession.partialPaymentsInfo && checkoutSession.partialPaymentsInfo.isPartialPaymentsEnabled;
  useEffect(() => {
    // Exceptionally track all initial loads via Metrics-js b/c we don't fire pageView
    // events to Ampltude to not conflict w/Landing Page view counts. This gets us a
    // raw count we can use to track volume in Grafana
    trackCheckoutSessionLoaded();
    if (isRecaptchaEnabled) {
      addRecaptchaScript(isATPortal);
    }
    if (!checkoutSession.sourceUrl || !checkoutSession.portalId) {
      return;
    }
    // Only add the tracking code + track pageView / formViews for UNPAID checkout
    // sessions. Paid checkout sessions should not fire analytic events
    addHsTrackingCode(checkoutSession, !!iFrameLayout);
    trackFormViewAnalyticEvent(checkoutSession, !!iFrameLayout);
    // Only execute this once so we add the script and run the tracking code once

    if (googleAnalyticsData && googleAnalyticsData.isEnabled) {
      addGoogleAnalyticsTrackingCode(googleAnalyticsData.measurementId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-render hook when window.__hsUserToken changes with pub/sub by subscribing `setHsUserToken` to published `addUserTokenListener` callback from analytics_js
  const [__, setHsUserToken] = useState(window.__hsUserToken);
  useEffect(() => {
    window._hsq = window._hsq || [];
    window._hsq.push(['addUserTokenListener', setHsUserToken]);
  }, []);
  const {
    debouncedCallCalculateTax: calculateTax
  } = useCalculateTax();
  const shouldCalculateTax = useCallback((name, value) => getShouldCalculateTaxOnAddrFieldChange(name, value, formState.billingAddress, checkoutSession), [formState, checkoutSession]);
  const handleChange = (name, {
    target: {
      value
    }
  }) => {
    trackFirstFormFieldInteraction({
      section: INTERACTION_SECTIONS.billingAddress,
      target: INTERACTION_TARGETS[name]
    });
    dispatch({
      type: 'setBillingAddressField',
      data: {
        name,
        value
      }
    });
    if (!collectShippingAddress && shouldCalculateTax(name, value)) {
      const billingAddress = Object.assign({}, formState.billingAddress, {
        line1: formState.billingAddress.address,
        line2: formState.billingAddress.address2,
        postalCode: name === 'postalCode' ? value : formState.billingAddress.postalCode,
        country: name === 'country' ? value : formState.billingAddress.country
      });
      void calculateTax({
        billingAddress
      });
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ContactInfo, {}), collectShippingAddress && /*#__PURE__*/_jsx(ShippingInfo, {}), isPartialPaymentsEnabled && /*#__PURE__*/_jsx(PaymentAmount, {}), /*#__PURE__*/_jsxs("div", {
      "data-tour-id": "payment-info-container",
      children: [/*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.paymentInfoHeader"
        })
      }), /*#__PURE__*/_jsx(PaymentElementSection, {
        resetSubmitError: submitObject.resetError
      }), /*#__PURE__*/_jsx(BillingInfo, {
        collectShippingAddress: collectShippingAddress,
        collectFullBillingAddress: collectFullBillingAddress,
        requireBillingAddressForACH: requireBillingAddressForACH,
        onChange: handleChange,
        stripePaymentMethod: stripePaymentMethod
      })]
    }), getIsAnyRecurring(checkoutSession.lineItems, selectedOptionalLineItems) && iFrameLayout === LAYOUTS.EMBED && /*#__PURE__*/_jsx(Total, {
      checkoutSession: checkoutSession
    }), shouldShowTermsOfServiceInputs && /*#__PURE__*/_jsx(TermsOfServiceInputs, {
      shouldShowTermsOfServiceAcknowledgementCheckbox: shouldShowTermsOfServiceAcknowledgementCheckbox,
      policySettings: policySettings,
      shouldDisplayPMOFCheckbox: shouldDisplayPMOFCheckbox,
      isZeroCheckout: isZeroCheckout,
      shouldShowClickToCancelCheckbox: shouldShowClickToCancelCheckbox
    }), /*#__PURE__*/_jsx(MaybeExpiredFutureLineItemsAlert, {}), /*#__PURE__*/_jsx(FormSubmitErrorMsgs, {
      submitEnabled: submitEnabled,
      showRequiredFieldsAlert: showRequiredFieldsAlert,
      setShowRequiredFieldsAlert: setShowRequiredFieldsAlert,
      submitObject: submitObject
    }), /*#__PURE__*/_jsx(ConfirmPaymentButton, {
      setShowRequiredFieldsAlert: setShowRequiredFieldsAlert,
      checkoutSession: checkoutSession,
      submitEnabled: submitEnabled,
      loadingBuyerOverrides: loadingBuyerOverrides,
      selectedOptionalLineItems: selectedOptionalLineItems,
      walletType: walletType,
      submitObject: submitObject,
      minimumAmountForPaymentMethod: minimumAmountForPaymentMethod,
      maximumAmountForPaymentMethod: maximumAmountForPaymentMethod
    }), shouldShowTermsOfServiceMessages && /*#__PURE__*/_jsx(TermsOfServiceMessages, {
      policySettings: policySettings,
      shouldDisplayPMOFMessage: shouldDisplayPMOFMessage,
      shouldShowFutureChargesMessage: shouldShowFutureChargesMessage,
      isZeroCheckout: isZeroCheckout
    }), /*#__PURE__*/_jsx(TLSEncryptionText, {})]
  });
};
export default PaymentContainer;